import { makeAutoObservable, runInAction } from "mobx";
import {
  createClient,
  RealtimeFrameUnion,
} from "@neontree/lovelica-api-client";
import { EdenWS } from "@elysiajs/eden/treaty";
import type { ChatTicketResult } from "services/coreapi";
import { reviveDates } from "utils/reviveDates";

export type RealtimeServiceEvent = RealtimeFrameUnion;

type EventHandler = (event: RealtimeServiceEvent) => void;

type ConnectionState = "idle" | "connecting" | "ready" | "closed" | "error";
type RealtimeClient = ReturnType<typeof createClient>;

type RealtimeServiceOptions = {
  client: RealtimeClient;
  /** A fresh single-use ticket for each socket (`?ticket=`). */
  getTicket: () => Promise<ChatTicketResult>;
  /**
   * The ticket call found no session: re-check it. Resolves true while the
   * user is still signed in, so the service keeps retrying.
   */
  onUnauthorized?: () => Promise<boolean>;
  onHttpResync?: () => Promise<void>;
  maxReconnectDelayMs?: number;
  baseReconnectDelayMs?: number;
};

/** One server frame from its wire text, or null when it isn't valid JSON. */
function parseFrame(data: unknown): RealtimeServiceEvent | null {
  if (typeof data !== "string") return null;
  try {
    return reviveDates(JSON.parse(data) as RealtimeServiceEvent);
  } catch (err) {
    console.error("RealtimeService: unparseable frame", err);
    return null;
  }
}

const isBrowserOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * One WebSocket to the platform API, reconnecting with backoff when it
 * drops. The server does not replay frames, so every gap is closed by an
 * HTTP resync (`onHttpResync`) — run *after* the new connection is ready,
 * so nothing emitted between the resync and `connection.ready` is lost, as
 * well as before each attempt, which doubles as a slow poll while the
 * socket can't be established at all.
 *
 * Sockets are tracked by identity: a handler only acts if its socket is
 * still `subscription`, so a socket closed by `disconnect()` (or replaced
 * by a newer `connect()`) can't reset state or schedule a reconnect on the
 * way out. `generation` does the same for the reconnect timer, which has
 * an `await` in it.
 *
 * The public connection fields are observable, so views (the
 * "Reconnecting…" notice) follow the state as it changes.
 */
export class RealtimeService {
  connectionState: ConnectionState = "idle";
  isConnected = false;
  /** Was connected at least once — a later drop is a *loss*, worth showing. */
  hasEverConnected = false;
  isManuallyStopped = false;

  userId: string | null = null;
  lastError: string | null = null;

  reconnectAttempt = 0;
  reconnectAt: number | null = null;

  private eventHandlers = new Set<EventHandler>();

  private subscription: EdenWS<any> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped by every `connect()`/`disconnect()`; async work checks it. */
  private generation = 0;

  private readonly client: RealtimeClient;
  private readonly getTicket: () => Promise<ChatTicketResult>;
  private readonly onUnauthorized?: () => Promise<boolean>;
  private readonly onHttpResync?: () => Promise<void>;
  private readonly maxReconnectDelayMs: number;
  private readonly baseReconnectDelayMs: number;

  constructor(options: RealtimeServiceOptions) {
    this.client = options.client;
    this.getTicket = options.getTicket;
    this.onUnauthorized = options.onUnauthorized;
    this.onHttpResync = options.onHttpResync;
    this.maxReconnectDelayMs = options.maxReconnectDelayMs ?? 20000;
    this.baseReconnectDelayMs = options.baseReconnectDelayMs ?? 750;

    // Only the connection state is observable; the plumbing (socket,
    // timer, handlers, options) is plain so MobX never proxies it.
    makeAutoObservable<
      this,
      | "eventHandlers"
      | "subscription"
      | "reconnectTimer"
      | "generation"
      | "client"
      | "getTicket"
      | "onUnauthorized"
      | "onHttpResync"
      | "maxReconnectDelayMs"
      | "baseReconnectDelayMs"
    >(this, {
      eventHandlers: false,
      subscription: false,
      reconnectTimer: false,
      generation: false,
      client: false,
      getTicket: false,
      onUnauthorized: false,
      onHttpResync: false,
      maxReconnectDelayMs: false,
      baseReconnectDelayMs: false,
    });
  }

  connect() {
    console.debug("RealtimeService: connecting...");

    if (
      this.connectionState === "connecting" ||
      this.connectionState === "ready"
    ) {
      return;
    }

    // An explicit connect supersedes any pending timed attempt.
    this.clearReconnectTimer();
    this.generation += 1;
    this.isManuallyStopped = false;
    this.connectionState = "connecting";
    this.lastError = null;

    void this.openWithTicket(this.generation);
  }

  /**
   * The socket can't carry the bearer token, so each attempt first trades it
   * for a single-use ticket. Never opens a socket without one.
   */
  private async openWithTicket(generation: number): Promise<void> {
    let result: ChatTicketResult;
    try {
      result = await this.getTicket();
    } catch (err) {
      console.warn("RealtimeService: ticket request failed", err);
      result = { ok: false, reason: "failed" };
    }
    // Stopped or superseded while the ticket was in flight: drop it.
    if (this.isManuallyStopped || this.generation !== generation) return;

    if (result.ok) {
      this.openSocket(result.ticket);
      return;
    }

    runInAction(() => {
      this.isConnected = false;
      this.lastError = `Realtime ticket: ${result.reason}`;
      this.connectionState =
        result.reason === "no-session" ? "closed" : "error";
    });
    if (result.reason === "no-session") {
      // The core API also answers 401 when it can't read a live session (its
      // database down), so only a confirmed logout stops the retries.
      if (!(await this.isStillSignedIn())) return;
      if (this.isManuallyStopped || this.generation !== generation) return;
    }
    this.scheduleReconnect();
  }

  private async isStillSignedIn(): Promise<boolean> {
    if (!this.onUnauthorized) return false;
    try {
      return await this.onUnauthorized();
    } catch (err) {
      // A check that failed is not a logout.
      console.warn("RealtimeService: session re-check failed", err);
      return true;
    }
  }

  private openSocket(ticket: string) {
    try {
      const sub = this.client.realtime.subscribe({ query: { ticket } });
      const isCurrent = () => this.subscription === sub;

      // Listen on the raw socket, not `sub.subscribe()`: Eden's wrapper
      // revives every date-looking string into a `Date` (a message whose
      // text is "12/05/2024" included) and ignores the client's `parseDate`
      // setting. Parse the frame ourselves and revive dates by key.
      sub.ws.addEventListener("message", (event) => {
        if (!isCurrent()) return;
        const message = parseFrame(event.data);
        if (!message) return;

        if (message.type === "connection.ready") {
          const isReconnect = this.hasEverConnected;
          runInAction(() => {
            this.userId = message.payload.userId;
            this.isConnected = true;
            this.hasEverConnected = true;
            this.connectionState = "ready";
            this.reconnectAttempt = 0;
            this.reconnectAt = null;
            this.lastError = null;
          });
          // Whatever happened while we were down (and between the
          // pre-connect resync and now) is only recoverable over HTTP.
          if (isReconnect) this.resync();
        } else if (message.type === "error") {
          const errorMessage =
            message.payload.message ?? "Realtime server error";
          runInAction(() => {
            this.lastError = errorMessage;
          });
        }

        this.emitEvent(message);
      });

      sub.on("open", () => {
        console.debug("RealtimeService: websocket opened");
      });

      sub.on("close", () => {
        console.debug("RealtimeService: websocket closed");
        // A socket we already let go of (disconnect, or replaced) must not
        // touch state or start a reconnect on its way out.
        if (!isCurrent()) return;

        runInAction(() => {
          this.subscription = null;
          this.isConnected = false;
          this.connectionState = "closed";
        });

        if (!this.isManuallyStopped) {
          this.scheduleReconnect();
        }
      });

      sub.on("error", (error) => {
        console.error("RealtimeService: websocket error", error);
        if (!isCurrent()) return;
        runInAction(() => {
          this.connectionState = "error";
          this.isConnected = false;
          this.lastError = "Realtime socket error";
        });
        // The close event that follows schedules the reconnect.
        sub.close();
      });

      this.subscription = sub;
    } catch (err) {
      console.error("RealtimeService: failed to create subscription", err);
      runInAction(() => {
        this.connectionState = "error";
        this.isConnected = false;
        this.lastError = String(err);
      });

      if (!this.isManuallyStopped) {
        this.scheduleReconnect();
      }
    }
  }

  disconnect() {
    this.isManuallyStopped = true;
    this.generation += 1;
    this.clearReconnectTimer();

    if (this.subscription) {
      const sub = this.subscription;
      // Drop it first, so its close/error handlers see a stale socket.
      this.subscription = null;
      try {
        sub.close();
      } catch {
        // subscription may already be closed
      }
    }

    this.isConnected = false;
    this.connectionState = "closed";
  }

  onEvent(handler: EventHandler): void {
    this.eventHandlers.add(handler);
  }

  offEvent(handler: EventHandler): void {
    this.eventHandlers.delete(handler);
  }

  /**
   * Fan a frame out to every handler. Each call is isolated: one handler
   * throwing (a malformed payload, a bug) must not stop the others from
   * seeing the frame.
   */
  private emitEvent(event: RealtimeServiceEvent): void {
    this.eventHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (err) {
        console.error("RealtimeService: event handler failed", event.type, err);
      }
    });
  }

  /** Fill the gap over HTTP; a failure here is not fatal to the socket. */
  private async resync(): Promise<void> {
    if (!this.onHttpResync) return;
    try {
      await this.onHttpResync();
    } catch (err) {
      console.error("RealtimeService: resync failed", err);
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();

    this.reconnectAttempt += 1;
    const exp = Math.min(
      this.baseReconnectDelayMs * 2 ** (this.reconnectAttempt - 1),
      this.maxReconnectDelayMs,
    );
    const jitter = Math.floor(Math.random() * 300);
    const delay = exp + jitter;

    this.reconnectAt = Date.now() + delay;

    const generation = this.generation;
    const isStale = () =>
      this.isManuallyStopped || this.generation !== generation;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (isStale()) return;

      // While the socket can't be established this resync is the only way
      // updates arrive; pointless (and noisy) while the browser knows it's
      // offline, though.
      if (!isBrowserOffline()) {
        await this.resync();
        // `disconnect()`/`connect()` may have run during the await.
        if (isStale()) return;
      }

      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
